import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchConsumerProviderInfo } from "../api/consumerProviderApi";
import { matchProviderToSubCategory } from "../utils/providerDisplay";
import { resolveTopicBackendIds } from "../utils/resolveTopicBackendIds";

/**
 * Loads provider details only for authenticated consumers.
 */
export function useTopicProvider({
  isAuthenticated,
  user,
  signupCategories,
  industryId,
  menuItem,
  enabled = true,
}) {
  const topicIds = useMemo(
    () => resolveTopicBackendIds(signupCategories, industryId, menuItem),
    [signupCategories, industryId, menuItem],
  );

  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    if (!enabled || !isAuthenticated || !user) {
      setProviders([]);
      setError(null);
      setLoading(false);
      return;
    }

    const consumerId = user.id ?? user.user_id;
    const userEmail = user.email ?? user.user_email ?? "";
    if (consumerId == null || !userEmail.trim()) {
      setProviders([]);
      setError(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    const result = await fetchConsumerProviderInfo({
      consumerId,
      userEmail,
    });

    if (!result.status) {
      setProviders([]);
      setError(result.message || "Unable to load provider details.");
    } else {
      setProviders(result.data);
      setError(null);
    }
    setLoading(false);
  }, [enabled, isAuthenticated, user]);

  useEffect(() => {
    load();
  }, [load]);

  const provider = useMemo(
    () => matchProviderToSubCategory(providers, topicIds?.subCategoryName),
    [providers, topicIds?.subCategoryName],
  );

  return {
    provider,
    providers,
    topicIds,
    loading,
    error,
    reload: load,
    canFetch: Boolean(isAuthenticated && user && (user.id ?? user.user_id)),
  };
}
